import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Container,
  Typography,
  Grid,
  Stack,
  Switch,
  Dialog,
  Button,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/material/styles';
import {
  AppWebsiteVisits,
} from '../sections/@dashboard/app';
import { Wallet } from '../sections/@dashboard/wallet';
import { Payment } from '../sections/@dashboard/razorpayPayment';

const StyledCard = styled('div')(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]         
      : theme.palette.background.paper, 

  padding: theme.spacing(2.5),
  borderRadius: 20,

  boxShadow:
    theme.palette.mode === 'light'
      ? '0px 0px 10px rgba(0, 0, 0, 0.15)'
      : '0px 0px 10px rgba(0, 0, 0, 0.6)',
}));


export default function WalletPage() {
  const [isAutoPayEnabled, setAutoPayEnabled] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [paymentInvoiceDate, setPaymentInvoiceData] = useState([]);
  const [paymentInvoiceBalance, setPaymentInvoiceBalance] = useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleToggle = () => {
    setConfirmationDialogOpen(true);
  };

  useEffect(() => {
    const toggleStatus = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/getAutoPayStatus`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.success === true) {
          setAutoPayEnabled(response.data.data);
        } else {
          console.error('Error Get Auto-Pay Status', response.data.message);
        }
      } catch (error) {
        console.error('Error Getting Auto-Pay Status', error);
      } finally {
        setIsLoading(false);
      }
    };

    toggleStatus();
  }, [token]);

  useEffect(() => {
    const fetchPaymentInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/getPaymentInvoice`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.success) {
          const paymentInvoiceDate = response.data.data1;
          const paymentInvoiceBalance = response.data.data2;
          setPaymentInvoiceData(paymentInvoiceDate);
          setPaymentInvoiceBalance(paymentInvoiceBalance);
        } else {
          console.error('API response indicates failure:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching monthly plan expenses data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchPaymentInvoice()
    }
  }, [token])

  const confirmToggle = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/updateAutoPayStatus`,
        { autoPay: !isAutoPayEnabled },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success === true) {
        toast.success(response.data.message, { autoClose: 2000 });
        setAutoPayEnabled(!isAutoPayEnabled);
      } else {
        console.error('Error Confirm Auto-Pay status', response.data.message);
        toast.error(response.data.message, { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error updating Auto-Pay wallets', error);
      toast.error('Error Confirm Auto-Pay Status ', error.message, { autoClose: 2000 });
    } finally {
      setIsLoading(false);
      setConfirmationDialogOpen(false);
    }
  };

  const cancelToggle = () => {
    setConfirmationDialogOpen(false);
  };

  const openForm = () => {
    setIsFormOpen(true);
  };

  const refresh = () => {
    setIsRefresh(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    navigate('/dashboard/wallet');
  };

  return (
    <>
      <Helmet>
        <title>Wallet | WatchNod</title>
        <meta
          name="description"
          content="Manage your wallet and subscriptions seamlessly on WatchNod. Turn AutoPay ON or OFF for hassle-free payments."
        />
        <meta name="keywords" content="Wallet, AutoPay, WatchNod, Subscriptions, Payments" />
      </Helmet>

      <Container maxWidth="xl">
        <header>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h3" component="h1" gutterBottom>
              Wallet
            </Typography>
            <Stack direction="row" alignItems="center">
              <Tooltip title="Auto Pay For Pay Automatic From Your Wallet" arrow>
                <IconButton color="primary" aria-label="Info">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
              {isAutoPayEnabled ? <>AutoPay On</> : <>AutoPay Off</>}
              <Switch
                onChange={handleToggle}
                checked={isAutoPayEnabled}
                name="autoPaySwitch"
                inputProps={{ 'aria-label': 'AutoPay Toggle' }}
              />
            </Stack>
          </Stack>
        </header>

        <Wallet openForm={openForm} refresh={isRefresh} />
        
        <StyledCard style={{ marginTop: '25px' }}>
          <Grid container>
            <Grid item xs={12} >
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <AppWebsiteVisits
                    title="Monthly Plan Expenses"
                    subheader="Total Pay Per Month in ($)"
                    chartLabels={paymentInvoiceDate}
                    chartData={[
                      {
                        name: 'Total Spent',
                        type: 'column',
                        fill: 'solid',
                        data: paymentInvoiceBalance,
                      },
                    ]}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </StyledCard>

        <Dialog open={isFormOpen} onClose={closeForm}>
          <StyledCard>
            <Payment open={openForm} closeForm={closeForm} />
          </StyledCard>
        </Dialog>

        <Dialog open={confirmationDialogOpen} onClose={cancelToggle}
          maxWidth="xs"
          fullWidth
          sx={{ padding: 2, '& .MuiDialog-paper': { padding: 2 } }}>
          <DialogTitle sx={{ fontSize: '1.25rem' }}>Confirm AutoPay Toggle</DialogTitle>

          <DialogContent>
            <Typography variant="body1">
              Are you sure you want to turn AutoPay <strong>{isAutoPayEnabled ? 'OFF' : 'ON'}</strong>?
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Stack direction="row" spacing={2}>
                <Button onClick={confirmToggle} variant="contained" color="primary">
                  Confirm
                </Button>
                <Button onClick={cancelToggle} variant="outlined" color="error">
                  Cancel
                </Button>
              </Stack>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
